import React, { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  MenuItem,
  type SelectChangeEvent,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { AddCircleOutlineOutlined, Check } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../components/common/ShowToast";
import CustomTextField from "../../../components/common/Textfield";
import PhoneNumberField from "../../../components/common/PhoneNumberField";
import PhotoEditor from "../../../components/PhotoEditor";
import { MuiTelInputInfo } from "mui-tel-input";

interface ICompany {
  id: number;
  name: string;
  primary_email?: string;
  phone?: string;
  address_one?: string;
  address_two?: string;
  suburb?: string;
  city?: string;
  country?: string;
  website?: string;
  facebook_profile?: string;
  instagram_profile?: string;
  twitter_profile?: string;
  linkedin_profile?: string;
}

interface ICompanyBranch {
  id: number;
  name: string;
  company_id: number;
}

interface IContactData {
  contact_type: "company";
  name: string;
  email: string;
  phone: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  country: string;
  website: string;
  notes: string;
  facebook_profile: string;
  instagram_profile: string;
  twitter_profile: string;
  linkedin_profile: string;
  company_id: number | null;
  company_branch: number | null;
  contact_image: string | null;
  contactImageFile: File | null;
}

interface IContactResponse {
  id: number;
  [key: string]: unknown;
}

const getErrorMessage = (err: unknown, fallback: string) => {
  const responseData = (err as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  if (!responseData || typeof responseData !== "object") {
    return fallback;
  }
  return Object.values(responseData).flat().join(" ") || fallback;
};

const EditCompanyContact: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [, setServerResponse] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [emailError, setEmailError] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<ICompany[]>([]);
  const [branchOptions, setBranchOptions] = useState<ICompanyBranch[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [openAddCompanyDialog, setOpenAddCompanyDialog] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState<Partial<ICompany>>({
    name: "",
    primary_email: "",
    phone: "",
  });
  const [savingNewCompany, setSavingNewCompany] = useState(false);

  const [form, setForm] = useState<IContactData>({
    contact_type: "company",
    name: "",
    email: "",
    phone: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "",
    country: "",
    website: "",
    notes: "",
    facebook_profile: "",
    instagram_profile: "",
    twitter_profile: "",
    linkedin_profile: "",
    company_id: null,
    company_branch: null,
    contact_image: null,
    contactImageFile: null,
  });

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith(".")) return "Local part cannot end with a dot.";
    return "";
  };

  useEffect(() => {
    const fetchContact = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await apiService.get<IContactData>(`/api/contacts/contact/${id}/`);
        setForm({
          contact_type: "company",
          name: response.data.name || "",
          email: response.data.email || "",
          phone: response.data.phone || "",
          address_one: response.data.address_one || "",
          address_two: response.data.address_two || "",
          suburb: response.data.suburb || "",
          city: response.data.city || "",
          country: response.data.country || "",
          website: response.data.website || "",
          notes: response.data.notes || "",
          facebook_profile: response.data.facebook_profile || "",
          instagram_profile: response.data.instagram_profile || "",
          twitter_profile: response.data.twitter_profile || "",
          linkedin_profile: response.data.linkedin_profile || "",
          company_id: response.data.company_id || null,
          company_branch: response.data.company_branch || null,
          contact_image: response.data.contact_image || null,
          contactImageFile: null,
        });
        setEmailError(validateEmail(response.data.email || ""));
      } catch (err: unknown) {
        console.error("Failed to fetch contact:", err);
        showErrorToast(
          getErrorMessage(err, "Failed to load contact details. Please try again.")
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchContact();
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchTerm.length >= 3) {
        fetchCompanies(companySearchTerm);
      } else {
        setCompanyOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchTerm]);

  useEffect(() => {
    return () => {
      if (form.contact_image && form.contact_image.startsWith("blob:")) {
        URL.revokeObjectURL(form.contact_image);
      }
    };
  }, [form.contact_image]);

  const fetchCompanies = async (search: string) => {
    setCompanyLoading(true);
    try {
      const companyResponse = await apiService.get<{ results: ICompany[] }>(
        `/api/companies/company/?search=${search}`
      );
      setCompanyOptions(companyResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch companies:", err);
      showErrorToast(
        getErrorMessage(err, "Failed to load companies. Please try again.")
      );
    } finally {
      setCompanyLoading(false);
    }
  };

  const fetchCompanyDetails = useCallback(async (companyId: number) => {
    try {
      const companyResponse = await apiService.get<ICompany>(
        `/api/companies/company/${companyId}/`
      );
      const company = companyResponse.data;
      setForm((prev) => ({
        ...prev,
        name: company.name || prev.name,
        primary_email: company.primary_email || prev.email,
        phone: company.phone || prev.phone,
        address_one: company.address_one || prev.address_one,
        address_two: company.address_two || prev.address_two,
        suburb: company.suburb || prev.suburb,
        city: company.city || prev.city,
        country: company.country || prev.country,
        website: company.website || prev.website,
        facebook_profile: company.facebook_profile || prev.facebook_profile,
        instagram_profile: company.instagram_profile || prev.instagram_profile,
        twitter_profile: company.twitter_profile || prev.twitter_profile,
        linkedin_profile: company.linkedin_profile || prev.linkedin_profile,
      }));
      setEmailError(company.primary_email ? validateEmail(company.primary_email) : "");
    } catch (err: unknown) {
      console.error("Failed to fetch company details:", err);
      showErrorToast(
        getErrorMessage(err, "Failed to load company details. Please try again.")
      );
    }
  }, []);

  useEffect(() => {
    if (form.company_id) {
      fetchCompanyDetails(form.company_id);
      fetchBranches(form.company_id);
    } else {
      resetFormFields();
      setBranchOptions([]);
      setForm((prev) => ({ ...prev, company_branch: null }));
    }
  }, [fetchCompanyDetails, form.company_id]);

  const fetchBranches = async (companyId: number) => {
    setBranchLoading(true);
    try {
      const branchResponse = await apiService.get<{ results: ICompanyBranch[] }>(
        `/api/companies/company-branch/?company_id=${companyId}`
      );
      setBranchOptions(branchResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch branches:", err);
      showErrorToast(
        getErrorMessage(err, "Failed to load branches. Please try again.")
      );
    } finally {
      setBranchLoading(false);
    }
  };

  const resetFormFields = () => {
    setForm((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      address_one: "",
      address_two: "",
      suburb: "",
      city: "",
      country: "",
      website: "",
      facebook_profile: "",
      instagram_profile: "",
      twitter_profile: "",
      linkedin_profile: "",
      notes: prev.notes,
      contact_image: prev.contact_image,
      contactImageFile: prev.contactImageFile,
    }));
    setEmailError("");
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
      setForm((prev) => ({ ...prev, email: value }));
    } else {
      setForm((prev) => ({ ...prev, [name as keyof IContactData]: value }));
    }
  };

  const handleSelectChangeWrapper = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    if (event && event.target && typeof event.target.value !== "string") {
      event.target.value = String(event.target.value);
    }
    handleChange(event as React.ChangeEvent<HTMLInputElement>);
  };

  const handlePhoneInputChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleCompanyChange = (event: React.SyntheticEvent, value: ICompany | null) => {
    setForm((prev) => ({
      ...prev,
      company_id: value?.id || null,
      company_branch: null,
    }));
  };

  const handleBranchChange = (event: React.SyntheticEvent, value: ICompanyBranch | null) => {
    setForm((prev) => ({
      ...prev,
      company_branch: value?.id || null,
    }));
  };

  const handleOpenAddCompanyDialog = () => {
    setOpenAddCompanyDialog(true);
    setNewCompanyData({ name: "", primary_email: "", phone: "" });
  };

  const handleCloseAddCompanyDialog = () => {
    setOpenAddCompanyDialog(false);
  };

  const handleNewCompanyChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewCompanyData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneCompanyInputChange = (
    value: string,
    _info: MuiTelInputInfo
  ) => {
    void _info;
    setNewCompanyData((prev) => ({ ...prev, phone: value }));
  };

  const handleSaveNewCompany = async () => {
    setSavingNewCompany(true);
    try {
      const res = await apiService.post<ICompany>(
        "/api/companies/company/",
        newCompanyData
      );
      const savedCompany = res.data;
      showSuccessToast("Company added successfully!");
      setCompanyOptions((prev) => [...prev, savedCompany]);
      setForm((prev) => ({
        ...prev,
        company_id: savedCompany.id,
        company_branch: null,
      }));
      handleCloseAddCompanyDialog();
    } catch (error: unknown) {
      console.error("Failed to save new company:", error);
      showErrorToast(getErrorMessage(error, "Failed to add company. Please try again."));
    } finally {
      setSavingNewCompany(false);
    }
  };

  const handleSaveContactImage = async (imageData: string, contactId?: number) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `contact_image_${contactId || "new"}.jpg`, {
        type: blob.type,
      });
      setForm((prev) => ({
        ...prev,
        contact_image: imageData,
        contactImageFile: file,
      }));
      showSuccessToast("Contact image saved successfully!");
    } catch (error: unknown) {
      console.error("Failed to process contact image:", error);
      showErrorToast("Failed to process contact image. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    setServerResponse(null);

    if (!form.name) {
      setServerResponse({
        success: false,
        message: "Company name is required.",
      });
      showErrorToast("Company name is required.");
      setIsSubmitting(false);
      return;
    }

    if (!form.email && !form.phone) {
      setServerResponse({
        success: false,
        message: "Provide at least one contact method (email or phone number).",
      });
      showErrorToast("Provide at least one contact method (email or phone number).");
      setIsSubmitting(false);
      return;
    }

    if (emailError) {
      setServerResponse({
        success: false,
        message: "Please correct the email address error.",
      });
      showErrorToast("Please correct the email address error.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("contact_type", form.contact_type);
    Object.entries(form).forEach(([key, value]) => {
      if (key === "contactImageFile" && value instanceof File) {
        formData.append("contact_image", value);
      } else if (key !== "contact_image" && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    try {
      const response = await apiService.patch<IContactResponse>(
        `/api/contacts/contact/${id}/`,
        formData,
        false
      );
      setServerResponse({
        success: true,
        message: "Company contact updated successfully.",
      });
      showSuccessToast("Company contact updated successfully.");

      setTimeout(() => {
        navigate(`/contacts/company/${response.data.id}`);
      }, 2000);
    } catch (err: unknown) {
      console.error("Failed to update company contact:", err);
      const errorMessage = getErrorMessage(
        err,
        "Failed to update company contact. Please try again."
      );
      setServerResponse({
        success: false,
        message: errorMessage,
      });
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailValid = !emailError && form.email.includes("@");

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": { borderColor: colors.primary[300] },
      "&.Mui-focused fieldset": { borderColor: colors.primary[400] },
    },
    "& .MuiInputLabel-root": { color: colors.primary[300] },
    "& .MuiInputBase-input": { color: colors.primary[700] },
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <Typography variant="h6">Loading company contact details...</Typography>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: 4,
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        background: colors.neutral[900],
        borderTop: `5px solid ${colors.secondary[50]}`,
        borderTopWidth: 5,
      }}
    >
      <Typography
        variant="h4"
        sx={{ color: colors.secondary[50], fontWeight: 700, mb: 3 }}
      >
        Edit Company Contact
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{ color: colors.primary[400], fontWeight: 600, mb: 3 }}
          >
            Contact Details
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isEmailValid && (
                        <Check sx={{ color: colors.primary[400] }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ ...textFieldStyles, "& .MuiFormHelperText-root": { color: colors.neutral[100] } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneNumberField
                fullWidth
                label="Phone Number"
                value={form.phone}
                onChange={handlePhoneInputChange}
                defaultCountry="ZW"
                variant="outlined"
                sx={{ marginBottom: "15px" }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={companyOptions}
                getOptionLabel={(option: ICompany) => option.name || ""}
                value={companyOptions.find((c) => c.id === form.company_id) || null}
                onChange={handleCompanyChange}
                onInputChange={(event, newInputValue) => {
                  setCompanySearchTerm(newInputValue);
                }}
                loading={companyLoading}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Company"
                    variant="outlined"
                    helperText="Type at least 3 characters to search companies"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companyLoading && (
                            <CircularProgress size={20} sx={{ color: colors.primary[400] }} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={textFieldStyles}
                  />
                )}
              />
              <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenAddCompanyDialog}
                sx={{
                  mt: 1,
                  color: colors.primary[400],
                  "&:hover": { backgroundColor: colors.neutral[800] },
                }}
              >
                Add Company
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={branchOptions}
                getOptionLabel={(option: ICompanyBranch) => option.name || ""}
                value={branchOptions.find((b) => b.id === form.company_branch) || null}
                onChange={handleBranchChange}
                disabled={!form.company_id || branchLoading}
                loading={branchLoading}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Company Branch"
                    variant="outlined"
                    helperText={form.company_id ? "Select a branch" : "Select a company first"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {branchLoading && (
                            <CircularProgress size={20} sx={{ color: colors.primary[400] }} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={textFieldStyles}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Website"
                name="website"
                value={form.website}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhotoEditor
                initialImage={form.contact_image}
                title="Company Logo"
                onSave={(imageData: string) => handleSaveContactImage(imageData)}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{ color: colors.primary[400], fontWeight: 600, mb: 3 }}
          >
            Address
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 1"
                name="address_one"
                value={form.address_one}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 2"
                name="address_two"
                value={form.address_two}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Suburb"
                name="suburb"
                value={form.suburb}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleSelectChangeWrapper}
                select
                sx={textFieldStyles}
              >
                <MenuItem value="" disabled sx={{ color: "#aaa" }}>
                  Select Country
                </MenuItem>
                <MenuItem value="Zimbabwe">Zimbabwe</MenuItem>
                <MenuItem value="South Africa">South Africa</MenuItem>
                <MenuItem value="Zambia">Zambia</MenuItem>
                <MenuItem value="Malawi">Malawi</MenuItem>
                <MenuItem value="Botswana">Botswana</MenuItem>
                <MenuItem value="Namibia">Namibia</MenuItem>
              </CustomTextField>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{ color: colors.primary[400], fontWeight: 600, mb: 3 }}
          >
            Social Media
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Facebook Profile"
                name="facebook_profile"
                value={form.facebook_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="LinkedIn Profile"
                name="linkedin_profile"
                value={form.linkedin_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="X Profile"
                name="twitter_profile"
                value={form.twitter_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Instagram Profile"
                name="instagram_profile"
                value={form.instagram_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{ color: colors.primary[400], fontWeight: 600, mb: 3 }}
          >
            Additional Info
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <CustomTextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={form.notes}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/contacts/company/${id}`)}
            disabled={isSubmitting}
            sx={{
              borderColor: colors.primary[400],
              color: colors.primary[400],
              "&:hover": {
                borderColor: colors.primary[300],
                backgroundColor: colors.primary[50],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: colors.primary[400],
              "&:hover": { backgroundColor: colors.primary[300] },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: colors.neutral[100] }} />
            ) : (
              "Save Contact"
            )}
          </Button>
        </Box>
      </form>

      <Dialog open={openAddCompanyDialog} onClose={handleCloseAddCompanyDialog}>
        <DialogTitle>Add New Company</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyData.name}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newCompanyData.primary_email}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <PhoneNumberField
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newCompanyData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneCompanyInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCompanyDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCompany}
            color="primary"
            disabled={savingNewCompany}
          >
            {savingNewCompany ? <CircularProgress size={24} /> : "Save Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default EditCompanyContact;